import React, { useMemo, useState } from "react";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import {
  useAcademicPrograms,
  useAllGrades,
  useAllStreams,
  useCreateAcademicStream
} from "../../../../hooks/api/education/useAcademicStructure";
import { formatEntityStatus } from "../common/academicUtils";

const ALL_GRADES = "";

const StreamList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const gradeFilter = searchParams.get("gradeId") ?? ALL_GRADES;
  const { data: programs = [] } = useAcademicPrograms();
  const { data: grades = [] } = useAllGrades();
  const {
    data: streams = [],
    isPending,
    refetch
  } = useAllStreams(gradeFilter || undefined);
  const createStream = useCreateAcademicStream();
  const [streamName, setStreamName] = useState("");
  const [createGradeId, setCreateGradeId] = useState(
    gradeFilter || grades[0]?.id || ""
  );

  const programNameById = useMemo(
    () => new Map(programs.map((program) => [program.id, program.name])),
    [programs]
  );

  const gradeLabel = (gradeId: string) => {
    const grade = grades.find((item) => item.id === gradeId);
    if (!grade) {
      return gradeId;
    }
    const programName = programNameById.get(grade.programId);
    return programName ? `${programName} · ${grade.name}` : grade.name;
  };

  const sortedStreams = useMemo(
    () =>
      [...streams].sort((a, b) =>
        gradeLabel(a.gradeId).localeCompare(gradeLabel(b.gradeId))
      ),
    [streams, grades, programNameById]
  );

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-streams", "Loading streams...")}
      />
    );
  }

  return (
    <Box data-testid="stream-list-page" sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {t("streams-title", "Streams")}
      </Typography>

      <TextField
        select
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 420 }}
        label={t("grade-filter-label", "Grade")}
        value={gradeFilter}
        onChange={(event) => {
          const value = event.target.value;
          if (value) {
            setSearchParams({ gradeId: value });
          } else {
            setSearchParams({});
          }
          setCreateGradeId(value || grades[0]?.id || "");
        }}
      >
        <MenuItem value={ALL_GRADES}>{t("all-grades", "All grades")}</MenuItem>
        {grades.map((grade) => (
          <MenuItem key={grade.id} value={grade.id}>
            {gradeLabel(grade.id)}
          </MenuItem>
        ))}
      </TextField>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField
          select
          size="small"
          sx={{ minWidth: 240 }}
          label={t("grade-select-label", "Grade")}
          value={createGradeId}
          onChange={(event) => setCreateGradeId(event.target.value)}
        >
          {grades.map((grade) => (
            <MenuItem key={grade.id} value={grade.id}>
              {gradeLabel(grade.id)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          fullWidth
          label={t("stream-name-label", "Stream name")}
          value={streamName}
          onChange={(event) => setStreamName(event.target.value)}
          disabled={!createGradeId}
        />
        <AppButton
          disabled={
            !createGradeId || !streamName.trim() || createStream.isPending
          }
          onClick={async () => {
            await createStream.mutateAsync({
              gradeId: createGradeId,
              name: streamName.trim()
            });
            setStreamName("");
            refetch();
          }}
        >
          {t("add-stream", "Add stream")}
        </AppButton>
      </Stack>

      {sortedStreams.length === 0 ? (
        <Typography color="text.secondary">
          {t("streams-list-empty", "No streams found.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {sortedStreams.map((stream) => (
            <Box
              component="li"
              key={stream.id}
              sx={{
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {stream.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {gradeLabel(stream.gradeId)}
                  {stream.status
                    ? ` · ${formatEntityStatus(stream.status)}`
                    : ""}
                </Typography>
              </Box>
              <AppButton variant="text" onClick={() => navigate(stream.id)}>
                {t("view", "View")}
              </AppButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default StreamList;
